import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Chip, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SkillChip from 'components/SkillChip'
import { ITag, Nullable } from 'types'
import { getGroupsByIndex } from 'services/job'
import { makeStyles } from 'tss-react/mui'

interface IUserSkillsCollectionProps extends React.HTMLAttributes<HTMLDivElement> {
  animated?: boolean
  alwaysOpen?: boolean
  hideExperience?: boolean
  userSkills?: {
    skillId: string
    name: string
    experience?: Nullable<number>
    required?: boolean
    requiredYears?: number
    groupIndex?: number
  }[]
  specializations?: {
    id: number
    name: string
    groupIndex?: number
  }[]
  tags?: ITag[]
  availableTags?: ITag[]
  children?: any
  icon?: any
  highlightedSkillIds?: string[]
}

const useStyles = makeStyles()({
  userSkillsCollection: {
    position: 'relative',
  },
  wrapper: {
    overflow: 'hidden',
    maxHeight: 37,
  },
  showMoreButton: {
    display: 'inline-block',
    cursor: 'pointer',
    color: 'rgb(0, 51, 204)',
  },
})

function UserSkillsCollection(props: IUserSkillsCollectionProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const {
    alwaysOpen = false,
    animated = true,
    hideExperience,
    userSkills = [],
    specializations = [],
    children,
    icon,
    highlightedSkillIds = [],
    ...others
  } = props

  const [showHiddenItems, setShowHiddenItems] = useState<boolean>(alwaysOpen)
  const [hasHiddenItems, setHasHiddenItems] = useState<boolean>(false)

  const innerDiv = useRef<HTMLDivElement>(null)

  const outerDiv = useRef<HTMLDivElement>(null)

  const actualizeHiddenItemsState = useCallback(() => {
    if (alwaysOpen) return
    if (!innerDiv.current) return

    if (innerDiv.current.clientHeight > 60) {
      // There are at least 2 rows of skills.
      if (!hasHiddenItems) {
        setHasHiddenItems(true)
      }
    } else {
      if (hasHiddenItems) {
        setHasHiddenItems(false)
      }
    }
  }, [alwaysOpen, hasHiddenItems, innerDiv])

  useEffect(() => {
    actualizeHiddenItemsState()
  }, [actualizeHiddenItemsState])

  const handleShowToggleClick = () => {
    setShowHiddenItems(!showHiddenItems)
  }

  const count = specializations.length + userSkills.length
  const highlightedSkillIdsSet = new Set(highlightedSkillIds)

  const requiredSkillGroups = getGroupsByIndex((userSkills).filter(s => s.required)).map(group => ({
    ...group,
    // sort skills by required experience, descending (i.e. most years first)
    skills: group.items.sort((a, b) => (b.requiredYears || 0) - (a.requiredYears || 0)),
  }))

  // sort other skills by experience, descending (i.e. most years first)
  const otherSkills = userSkills
    .filter(s => !s.required)
    .sort((a, b) => (b.experience || 0) - (a.experience || 0))

  const specializationGroups = getGroupsByIndex(specializations)

  return (
    <div className={classes.userSkillsCollection} data-cy="user-skills-collection" {...others}>
      <div
        className={classes.wrapper}
        ref={outerDiv}
        style={showHiddenItems ? { maxHeight: 1000, transition: 'max-height 1.5s linear' } : undefined}
      >
        <div ref={innerDiv}>
          {specializationGroups?.map((group) => {
            const showSeparator = specializationGroups.length > 1 && group.index !== specializationGroups.length - 1
            return (
              <React.Fragment key={group.index}>
                {group.items.map(s => (
                  <Tooltip key={s.id} title={`${s.name} (specialization)`}>
                    <div style={{ display: 'inline-block', margin: theme.spacing(0, 1, 1, 0) }}>
                      <Chip
                        data-cy={`freelancer-chip-specialization-${s.name}`}
                        style={{ background: theme.palette.secondary.main }}
                        label={(
                          <span style={{ color: 'white' }}>
                            {s.name}
                          </span>
                      )}
                      />
                    </div>
                  </Tooltip>
                ))}
                {showSeparator && (
                  <SkillChip flat animated={animated} skill_experience={{ name: 'Or' }} />
                )}
              </React.Fragment>
            )
          })}

          {requiredSkillGroups?.map((group) => {
            const showSeparator = requiredSkillGroups.length > 1 && group.index !== requiredSkillGroups.length - 1
            return (
              <React.Fragment key={group.index}>
                {group.skills.map(s => (
                  <SkillChip
                    icon={icon}
                    key={s.skillId}
                    skill_experience={s}
                    hideExperience={hideExperience}
                    animated={animated}
                    required
                    focused={highlightedSkillIdsSet.size > 0 && highlightedSkillIdsSet.has(s.skillId)}
                  />
                ))}
                {showSeparator && (
                  <SkillChip flat animated={animated} skill_experience={{ name: 'Or' }} />
                )}
              </React.Fragment>
            )
          })}

          {requiredSkillGroups.length > 0 && otherSkills.length > 0 && (
            <SkillChip flat animated={animated} skill_experience={{ name: 'Nice to have:' }} />
          )}

          {otherSkills?.map(s => (
            <SkillChip
              icon={icon}
              key={s.skillId}
              skill_experience={s}
              hideExperience={hideExperience}
              animated={animated}
              focused={highlightedSkillIdsSet.size > 0 && highlightedSkillIdsSet.has(s.skillId)}
            />
          ))}

          {children}
        </div>
      </div>

      {hasHiddenItems && (
        <Typography variant="body1" component="div" className={classes.showMoreButton} onClick={handleShowToggleClick} role="button">
          {showHiddenItems && 'Hide'}
          {!showHiddenItems && `Show all ${count} items`}
        </Typography>
      )}
    </div>
  )
}

export default UserSkillsCollection
